import React, { useState, useEffect } from 'react';
import { Search, Filter, Phone as PhoneIcon, Mail, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Student {
    ID: number;
    Username: string; // Email
    FullName: string;
    Country: string;
    Phone: string;
}

const StudentList = () => {
    const navigate = useNavigate();
    const [students, setStudents] = useState<Student[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchStudents();
    }, []);

    const fetchStudents = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/admin/students', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setStudents(data || []);
            }
        } catch (error) {
            console.error("Failed to fetch students");
        } finally {
            setLoading(false);
        }
    };

    const filteredStudents = students.filter(s =>
        (s.FullName && s.FullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (s.Username && s.Username.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const getInitials = (name: string) => {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.charAt(0).toUpperCase();
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Header */}
            <div className="p-8 border-b border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h3 className="text-xl font-bold text-[#011E26]">Student Admissions</h3>
                    <p className="text-sm text-slate-400 mt-1">Manage and track student application lifecycles.</p>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <div className="relative flex-1 md:flex-none">
                        <Search className="absolute left-3.5 top-2.5 text-slate-400" size={16} />
                        <input
                            type="text"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009B8F]/20 focus:border-[#009B8F]/30 outline-none transition-all w-full md:w-48"
                        />
                    </div>
                    <button className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-400 transition-colors">
                        <Filter size={16} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50/50 border-b border-slate-100">
                            <th className="px-8 py-4">Student Profile</th>
                            <th className="px-8 py-4">Contact Info</th>
                            <th className="px-8 py-4">Assigned Country</th>
                            <th className="px-8 py-4">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan={4} className="text-center py-20 text-[#009B8F] font-semibold">Loading Database...</td></tr>
                        ) : filteredStudents.length === 0 ? (
                            <tr><td colSpan={4} className="text-center py-20 text-slate-300 italic text-sm">No student matching your search criteria.</td></tr>
                        ) : (
                            filteredStudents.map((student) => (
                                <tr key={student.ID} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-[#009B8F] flex items-center justify-center text-white font-bold text-xs shrink-0">
                                                {getInitials(student.FullName)}
                                            </div>
                                            <div>
                                                <div className="font-semibold text-[#011E26] text-sm">{student.FullName || 'Anonymous'}</div>
                                                <div className="text-[10px] text-slate-400 font-mono mt-0.5">NEX-{student.ID.toString().padStart(4, '0')}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="space-y-1.5">
                                            <div className="text-xs text-[#011E26] flex items-center gap-2">
                                                <PhoneIcon size={12} className="text-slate-400 shrink-0" />
                                                <span>{student.Phone || 'No phone'}</span>
                                            </div>
                                            <div className="text-xs text-slate-500 flex items-center gap-2">
                                                <Mail size={12} className="text-slate-400 shrink-0" />
                                                <span>{student.Username}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <span className="inline-block px-3 py-1 bg-[#009B8F] text-white rounded-full text-[10px] font-bold tracking-wide">
                                            {student.Country || 'Unspecified'}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5">
                                        <button
                                            onClick={() => navigate(`/admin/student/${student.ID}`)}
                                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#009B8F] hover:bg-[#00857a] px-4 py-2 rounded-lg transition-colors"
                                        >
                                            View Case <ChevronRight size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Footer */}
            <div className="px-8 py-4 bg-slate-50/30 border-t border-slate-100 flex justify-between items-center text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                <span>Displaying {filteredStudents.length} Students</span>
                <span className="text-[#009B8F]">NexAI Global Intelligence</span>
            </div>
        </div>
    );
};

export default StudentList;
